import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import PaginatedTable from "../Common/PaginatedTable";
import { SearchResult } from "../../Models/SearchResult";
import { formatNumbers } from "../../Utils";
import { Button, Tooltip } from "flowbite-react";
import { Link } from "react-router-dom";
import EmailModal from "../Forms/WarehouseAvailability";
import { useAppSelector } from "../../Reducers/store";
import { EquipmentTypeEnums } from "../../Models/EquipmentTypeEnums";
import OrderApi from "../../Api/OrderApi";
import { Order } from "../../Models/Order";
import { toast } from "react-toastify";
import { OrderItem } from "../../Models/OrderItem";

export default function SearchListTable({
  data,
  equipmentType,
}: {
  data: SearchResult[];

  equipmentType: EquipmentTypeEnums;
}) {
  const columnHelper = createColumnHelper<SearchResult>();
  const { userModel } = useAppSelector((state) => state.user);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | null>(
    null
  );

  const handleOpenEmailModal = (id: number) => {
    setSelectedEquipmentId(id);
    setShowEmailModal(true);
  };
  const formatPrice = (value: number | null | undefined) => {
    if (value === null || value === undefined || value === 0) return "";
    return `$${formatNumbers(value)}`;
  };
  const handleAddItemClick = async (equipmentId: number) => {
    if (userModel) {
      let openOrderId =
        localStorage.getItem("openOrderId") &&
        Number(localStorage.getItem("openOrderId")) >= 0
          ? Number(localStorage.getItem("openOrderId"))
          : 0;
      if (openOrderId === 0) {
        await OrderApi.checkUserOpenOrder(userModel.id)
          .then((res) => {
            if (res.data) {
              openOrderId = res.data.id;
              localStorage.setItem("openOrderId", openOrderId.toString());
            } else {
              const newOrder: Order = {
                id: 0,
                businessName: null,
                contactName: null,
                contactPhone: null,
                contactEmail: null,
                notes: null,
                dateCreated: null,
                orderStatusId: 1,
                userId: userModel.id,
                billingAddressId: null,
                shippingAddressId: null,
                dateSubmitted: null,
                ponumber: null,
                clientId: userModel.clientId,
                shippingMethodId: 15,

                client: null,
                orderItems: [],
                billingAddress: null,
                shippingAddress: null,
                user: null,
              };
              OrderApi.addUpdateOrder(newOrder)
                .then((res) => {
                  if (res.data.success) {
                    openOrderId = Number(res.data.affectedEntityIdentifier);
                    localStorage.setItem("openOrderId", openOrderId.toString());
                  }
                })
                .catch((err) => {
                  toast.error("Item not added to cart: " + err);
                  setIsLoading(false);
                });
            }
          })
          .catch((err) => {
            toast.error("Item not added to cart: " + err);
            setIsLoading(false);
          });
      }
      const newOrderItem: OrderItem = {
        id: 0,
        orderId: openOrderId,
        equipmentId: equipmentId,
        quantity: 1,
        equipment: null,
      };
      await OrderApi.addUpdateOrderItem(newOrderItem)
        .then(async (res) => {
          if (res.data.success) {
            toast.success("Item added to cart");
            await OrderApi.getOrderItemsCount(openOrderId)
              .then((res) => {
                if (res.data !== null) {
                  localStorage.setItem("orderItemsCount", res.data.toString());
                  window.dispatchEvent(new Event("updateOrderItemCount"));
                }
              })
              .catch((err) => {
                toast.error("Item not added to cart: " + err);
                setIsLoading(false);
              });
          } else {
            toast.error("Item not added to cart - Please try again.");
            //localStorage.setItem('openOrderId', '0');
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("Item not added to cart: " + err);
          setIsLoading(false);
        });
    }
  };

  const columns = [
    columnHelper.accessor((row) => row.imageURL || row.manufacturerImageURL, {
      id: "image",
      header: "Image",
      cell: (info) => (
        <img
          src={info.getValue() ?? ""}
          alt="Item"
          style={{ width: "100px" }}
        />
      ),
      enableColumnFilter: false,
    }),
    columnHelper.accessor("equipmentName", {
      id: "equipmentName",
      header: "Name",
      cell: (info) => info.getValue() ?? "",
      filterFn: "includesString",
    }),
    columnHelper.accessor("manufacturerName", {
      id: "manufacturerName",
      header: "Manufacturer",
      filterFn: "includesString",
    }),
    ...(equipmentType !== 2
      ? [
          columnHelper.accessor("categoryName", {
            id: "categoryName",
            header: "Category",
            filterFn: "includesString",
          }),
          columnHelper.accessor("subCategoryName", {
            header: "SubCategory",
            id: "subCategoryName",
            filterFn: "includesString",
          }),
        ]
      : []),
    columnHelper.accessor("description", {
      id: "description",
      header: "Description",
      filterFn: "includesString",
    }),
    ...(userModel
      ? [
          columnHelper.accessor("afs", {
            id: "afs",
            header: "AFS",
            filterFn: "inNumberRange",
          }),
        ]
      : []),
    // ✅ Always show List Price
    columnHelper.accessor("listPrice", {
      id: "listPrice",
      header: "List",
      filterFn: "inNumberRange",
      enableColumnFilter: true,
      cell: (info) =>
        info.row.original.listPrice != null
          ? `$${formatNumbers(info.row.original.listPrice)}`
          : "",
    }),
    // ✅ Conditionally add price columns if user is logged in
    ...(userModel
      ? [
          ...(equipmentType !== 2
            ? [
                columnHelper.accessor("tradePrice", {
                  id: "tradePrice",
                  header: "Trade",
                  filterFn: "inNumberRange",
                  cell: (info) =>
                    formatPrice(info.getValue()) !== undefined
                      ? `$${formatNumbers(info.getValue())}`
                      : "",
                }),
              ]
            : []),
          columnHelper.accessor("special1Price", {
            id: "special1Price",
            header: "FTL",
            filterFn: "inNumberRange",
            cell: (info) => formatPrice(info.getValue()),
          }),
          ...(equipmentType !== 2
            ? [
                columnHelper.accessor("special2Price", {
                  id: "special2Price",
                  header: "NET",
                  filterFn: "inNumberRange",
                  cell: (info) => formatPrice(info.getValue()),
                }),
              ]
            : []),
        ]
      : []),
    columnHelper.accessor((row) => row.equipmentId, {
      id: "additem",
      header: "Add To Cart",
      size: 110,
      enableColumnFilter: false,
      cell: (info) => (
        <div className="flex flex-col gap-2 items-center w-full">
          {userModel && (
            <>
              <div className="w-full block">
                <Button
                  type="button"
                  size="xs"
                  onClick={() =>
                    handleAddItemClick(info.row.original.equipmentId)
                  }
                  className="buttonMain w-full"
                >
                  Add to Cart
                </Button>
              </div>
              <div className="w-full block">
                <Button
                  type="button"
                  className="buttonMain w-full"
                  size="xs"
                  onClick={() =>
                    handleOpenEmailModal(info.row.original.equipmentId)
                  }
                >
                  Check Availability
                </Button>
              </div>
            </>
          )}
          {!userModel && (
            <div className="w-full block">
              <Link to={"/login"}>
                <Button type="button" className="buttonMain w-full" size="xs">
                  Login to order
                </Button>
              </Link>
            </div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.equipmentId, {
      id: "actions",
      header: "View",
      size: 50,
      enableColumnFilter: false,
      cell: (info) => (
        <div className="flex flex-col gap-2 items-center w-full">
          <div className="w-full block">
            <Button
              size="xs"
              className="buttonMain"
              fullSized
              onClick={() =>
                (window.location.href = `/products/equipment/${info.getValue()}`)
              }
            >
              View Details
            </Button>
          </div>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      sorting: [
        {
          id: "equipmentName",
          desc: false,
        },
      ],
    },
  });

  return (
    <>
      <style>
        {`
      thead th {
        background-color: white !important;
        position: sticky;
        top: 0;
        z-index: 10;
      }
    `}
      </style>
      <PaginatedTable
        table={table}
        dataCount={data.length}
        columnResizeMode="onChange"
        showFilters={true}
      />
      {selectedEquipmentId !== null && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          equipment={{ id: selectedEquipmentId } as any} // Fill in full model if needed
          onSendSuccess={() => alert("Email sent!")}
        />
      )}
    </>
  );
}
